import React, { useState, useEffect } from "react";
import {
 Box,
 Button,
 TextField,
 Typography,
 Alert,
 AlertTitle,
 IconButton,
 CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, forgotPassword } from "../redux/auth/action";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/system";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Styled components for layout
const LeftContainer = styled("div")({
 backgroundColor: "#FFFFFF",
 padding: "40px 60px",
 width: "50%",
 height: "100vh",
 display: "flex",
 flexDirection: "column",
 justifyContent: "center",
 boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
 borderRadius: "10px",
});

const RightContainer = styled("div")({
 backgroundImage:
  "url(https://car-expert-media.s3.ap-south-1.amazonaws.com/1748372704897-main.webp)",
 backgroundSize: "cover",
 backgroundPosition: "center",
 width: "50%",
 height: "100vh",
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 borderTopRightRadius: "10px",
 borderBottomLeftRadius: "10px",
 color: "white",
});

const CustomButton = styled(Button)({
 backgroundColor: "#000D6B",
 color: "#fff",
 "&:hover": {
  backgroundColor: "#3700b3",
 },
 borderRadius: "50px",
 padding: "12px 20px",
 fontSize: "16px",
 marginTop: "20px",
});

const CustomTextField = styled(TextField)({
 backgroundColor: "#fff",
 borderRadius: "8px",
 marginBottom: "16px",
 "& .MuiInputBase-root": {
  borderRadius: "8px",
 },
 "& .MuiFormLabel-root": {
  fontSize: "14px",
  color: "#555",
 },
 "& .MuiInputBase-input": {
  fontSize: "16px",
  padding: "12px 15px",
 },
});

const LoginRegister = () => {
 const navigate = useNavigate();
 const dispatch = useDispatch();
 const [formData, setFormData] = useState({
  email: "",
  password: "",
 });
 const [alert, setAlert] = useState({
  visible: false,
  message: "",
  severity: "",
 });
 const { userDetails, role, isAuth } = useSelector((store) => store.auth);
 const [showPassword, setShowPassword] = useState(false);
 const [isLoading, setIsLoading] = useState(false);

 const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData({ ...formData, [name]: value });
 };

 // Login function
 const handleLogin = async () => {
  setIsLoading(true);
  try {
   const response = await dispatch(
    loginUser(formData.email, formData.password),
   );
   if (response.success) {
    setAlert({
     visible: true,
     message: "Login successful",
     severity: "success",
    });
   } else {
    setAlert({
     visible: true,
     message: "Login failed. Please check your credentials.",
     severity: "error",
    });
   }
  } catch (error) {
   setAlert({
    visible: true,
    message: "Error during login. Please try again.",
    severity: "error",
   });
  } finally {
   setIsLoading(false);
  }
 };

 // Forgot password logic
 const handleForgotPassword = async () => {
  if (!formData.email) {
   setAlert({
    visible: true,
    message: "Please enter your email to reset your password.",
    severity: "warning",
   });
   return;
  }

  try {
   const response = await fetch(
    "https://app-api.carexpert.org.in/api/forgot-password",
    {
     method: "POST",
     headers: {
      "Content-Type": "application/json",
     },
     body: JSON.stringify({ email: formData.email }),
    },
   );

   const data = await response.json();

   if (response.ok) {
    setAlert({
     visible: true,
     message: "Password reset email sent. Please check your inbox.",
     severity: "success",
    });
   } else {
    setAlert({
     visible: true,
     message: data.message || "Failed to send reset email.",
     severity: "error",
    });
   }
  } catch (error) {
   setAlert({
    visible: true,
    message: "Error occurred while sending reset email. Please try again.",
    severity: "error",
   });
  }
 };

 // Redirection logic after login
 useEffect(() => {
  if (isAuth) {
   if (
    userDetails?.vendor?.role === "admin" ||
    userDetails?.vendor?.role === "vendor"
   ) {
    navigate("/dashboard");
   } else if (userDetails?.vendor?.role === "user") {
    setAlert({
     visible: true,
     message: "Your account is not verified as a vendor",
     severity: "error",
    });
   }
  }
 }, [role, isAuth, navigate]);

 return (
  <Box display="flex" height="100vh">
   {/* Left Container - Login Form */}
   <LeftContainer>
    <Box mb={4}>
     <Typography variant="h4" fontWeight="bold" color="#333" gutterBottom>
      Car-Expert
     </Typography>
     <Typography variant="body1" color="#666">
      Welcome to Car-Expert – Login as admin
     </Typography>
    </Box>

    {alert.visible && (
     <Alert
      severity={alert.severity}
      onClose={() => setAlert({ visible: false, message: "", severity: "" })}
      sx={{ marginBottom: "20px" }}
     >
      <AlertTitle>
       {alert.severity === "success" ? "Success" : "Error"}
      </AlertTitle>
      {alert.message}
     </Alert>
    )}

    <CustomTextField
     fullWidth
     label="Email"
     margin="normal"
     type="email"
     name="email"
     value={formData.email}
     onChange={handleInputChange}
    />
    <CustomTextField
     fullWidth
     label="Password"
     margin="normal"
     type={showPassword ? "text" : "password"}
     name="password"
     value={formData.password}
     onChange={handleInputChange}
     InputProps={{
      endAdornment: (
       <IconButton
        onClick={() => setShowPassword((prev) => !prev)}
        edge="end"
        sx={{ color: "#888" }}
       >
        {showPassword ? <VisibilityOff /> : <Visibility />}
       </IconButton>
      ),
     }}
    />

    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
     <Button onClick={handleForgotPassword} sx={{ color: "#000D6B" }}>
      Forgot password?
     </Button>
    </Box>

    {isLoading ? (
     <CircularProgress sx={{ margin: "20px auto", display: "block" }} />
    ) : (
     <CustomButton fullWidth variant="contained" onClick={handleLogin}>
      Sign in
     </CustomButton>
    )}
   </LeftContainer>

   {/* Right Container - Background Image */}
   <RightContainer></RightContainer>
  </Box>
 );
};

export default LoginRegister;
